import React, { useState, useRef } from 'react';
import { Send, CheckCircle, Building2, Upload, X, FileText } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Phone number formatting utility
const formatPhoneNumber = (value) => {
  const phoneNumber = value.replace(/\D/g, '');
  const limitedNumber = phoneNumber.slice(0, 10);
  
  if (limitedNumber.length === 0) return '';
  if (limitedNumber.length <= 3) return `(${limitedNumber}`;
  if (limitedNumber.length <= 6) return `(${limitedNumber.slice(0, 3)}) ${limitedNumber.slice(3)}`;
  return `(${limitedNumber.slice(0, 3)}) ${limitedNumber.slice(3, 6)}-${limitedNumber.slice(6)}`;
};

// Email validation utility
const isValidEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

const VendorApplicationPage = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    website: '',
    trade_type: '',
    years_in_business: '',
    license_number: '',
    service_area: '',
    description: ''
  });
  const [w9File, setW9File] = useState(null);
  const [insuranceFile, setInsuranceFile] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  const w9InputRef = useRef(null);
  const insuranceInputRef = useRef(null);

  const tradeTypes = [
    'Electrical', 'Plumbing', 'HVAC', 'Roofing', 'Flooring', 'Painting',
    'Drywall', 'Framing', 'Concrete', 'Landscaping', 'Windows & Doors',
    'Cabinetry', 'Tile & Stone', 'Insulation', 'Other'
  ];

  const acceptedFileTypes = '.pdf,.doc,.docx,.jpg,.jpeg,.png';
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e, setFile, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > maxFileSize) {
        setErrors({ ...errors, [fieldName]: 'File size must be less than 10MB' });
        return;
      }
      setFile(file);
      if (errors[fieldName]) {
        setErrors({ ...errors, [fieldName]: null });
      }
    }
  };

  const removeFile = (setFile, inputRef) => {
    setFile(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create FormData for file upload
      const submitData = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });
      
      // Add files if present
      if (w9File) {
        submitData.append('w9_file', w9File);
      }
      if (insuranceFile) {
        submitData.append('insurance_file', insuranceFile);
      }
      
      await axios.post(`${BACKEND_URL}/api/vendors`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setIsSubmitted(true);
      
      // Show success toast
      toast.success('Application Submitted Successfully!', {
        description: 'Thank you for your interest in partnering with C3 Contracting. We will review your application and contact you soon.',
        duration: 5000,
      });
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Submission Failed', {
        description: 'There was an error submitting your application. Please try again.',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      setFormData({ ...formData, [name]: formatPhoneNumber(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const FileUploadField = ({ label, file, setFile, inputRef, fieldName, required = false }) => (
    <div>
      <Label className="text-slate-700 mb-2 block">{label} {required && '*'}</Label>
      <div className="relative">
        {file ? (
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <FileText className="w-5 h-5 text-cyan-600 flex-shrink-0" />
            <span className="text-sm text-slate-700 truncate flex-1">{file.name}</span>
            <button
              type="button"
              onClick={() => removeFile(setFile, inputRef)}
              className="p-1 hover:bg-slate-200 rounded-full transition-colors"
            >
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>
        ) : (
          <div
            onClick={() => inputRef.current?.click()}
            className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-cyan-500 hover:bg-cyan-50/50 transition-colors"
          >
            <Upload className="w-8 h-8 text-slate-400 mb-2" />
            <span className="text-sm text-slate-600 font-medium">Click to upload</span>
            <span className="text-xs text-slate-400 mt-1">PDF, DOC, DOCX, JPG, PNG (max 10MB)</span>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={acceptedFileTypes}
          onChange={(e) => handleFileChange(e, setFile, fieldName)}
          className="hidden"
        />
      </div>
      {errors[fieldName] && <p className="text-red-500 text-sm mt-1">{errors[fieldName]}</p>}
    </div>
  );

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-52 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-1/2 w-96 h-96 bg-cyan-500 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Building2 className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Vendor Application
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Join our network of trusted vendors and subcontractors. We're always looking for skilled professionals who share our commitment to quality and integrity.
          </p>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Application Received!</h3>
                <p className="text-slate-600">
                  Thank you for your interest in partnering with C3 Contracting. We'll review your application and get back to you within 5-7 business days.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Company Information</h2>
                  <p className="text-slate-600">Please fill out the form below to apply as a vendor.</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-slate-700 mb-2 block">Company Name *</Label>
                    <Input name="company_name" value={formData.company_name} onChange={handleChange} required className="h-12" />
                  </div>
                  <div>
                    <Label className="text-slate-700 mb-2 block">Contact Name *</Label>
                    <Input name="contact_name" value={formData.contact_name} onChange={handleChange} required className="h-12" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-slate-700 mb-2 block">Email *</Label>
                    <Input 
                      name="email" 
                      type="email" 
                      value={formData.email} 
                      onChange={handleChange} 
                      required 
                      className={`h-12 ${errors.email ? 'border-red-500' : ''}`}
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <Label className="text-slate-700 mb-2 block">Phone *</Label>
                    <Input 
                      name="phone" 
                      type="tel" 
                      value={formData.phone} 
                      onChange={handleChange} 
                      required 
                      className={`h-12 ${errors.phone ? 'border-red-500' : ''}`}
                      maxLength={14}
                    />
                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                  </div>
                </div>

                <div>
                  <Label className="text-slate-700 mb-2 block">Website</Label>
                  <Input name="website" type="url" value={formData.website} onChange={handleChange} className="h-12" />
                </div>

                <div className="border-t border-slate-200 pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Trade Information</h3>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-slate-700 mb-2 block">Trade/Service Type *</Label>
                    <Select value={formData.trade_type} onValueChange={(v) => setFormData({ ...formData, trade_type: v })}>
                      <SelectTrigger className="h-12"><SelectValue placeholder="Select your trade" /></SelectTrigger>
                      <SelectContent>
                        {tradeTypes.map((trade) => (<SelectItem key={trade} value={trade}>{trade}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-700 mb-2 block">Years in Business *</Label>
                    <Input name="years_in_business" type="number" value={formData.years_in_business} onChange={handleChange} required className="h-12" min="0" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-slate-700 mb-2 block">License Number</Label>
                    <Input name="license_number" value={formData.license_number} onChange={handleChange} className="h-12" />
                  </div>
                  <div>
                    <Label className="text-slate-700 mb-2 block">Service Area *</Label>
                    <Input name="service_area" value={formData.service_area} onChange={handleChange} required className="h-12" />
                  </div>
                </div>

                <div>
                  <Label className="text-slate-700 mb-2 block">Tell Us About Your Company *</Label>
                  <Textarea name="description" value={formData.description} onChange={handleChange} required rows={5} />
                </div>

                {/* Document Uploads Section */}
                <div className="border-t border-slate-200 pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Required Documents</h3>
                  <p className="text-sm text-slate-600 mb-6">Please upload your W-9 form and Certificate of Insurance.</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <FileUploadField
                    label="W-9 Form"
                    file={w9File}
                    setFile={setW9File}
                    inputRef={w9InputRef}
                    fieldName="w9"
                  />
                  <FileUploadField
                    label="Certificate of Insurance"
                    file={insuranceFile}
                    setFile={setInsuranceFile}
                    inputRef={insuranceInputRef}
                    fieldName="insurance"
                  />
                </div>

                <Button type="submit" size="lg" disabled={isSubmitting} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold h-12">
                  {isSubmitting ? 'Submitting...' : <><Send className="mr-2 w-5 h-5" /> Submit Application</>}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VendorApplicationPage;
